import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import get from 'lodash/get';
import dayjs from 'dayjs';

import type UserService from '~/services/UserService';
import Constants from '~/config/Constants';
import FeatureFlags from '~/services/FeatureFlagsService';
import { getAccountType } from '~/utils/UserUtils';
import UserActionsService from '~/services/apis/UserActionsService';
import eventBus from '~/services/EventBus';
import $axios from '~/services/AxiosService';
import { isOtherOrganization } from '~/utils/OrgUtils';
import $store from '~/services/StoreService';

// tracking new user activations
const NEW_USER_ACTIVATION_TRACKING_DATE = dayjs('2024-04-18T00:00:00.00Z');

export default function useUserDerivedDetails() {
  const store = $store();
  const user = computed(() => (store?.getters?.['root/userService']) as UserService);

  const hasPrivateContentAccess = computed(() => get(
    user.value.subscription || user.value.subscriptions[0],
    'priviledges.privateContent',
    false,
  ));

  const isLoggedIn = computed(() => user.value.isLoggedIn);

  const isE2ETestAccount = computed(() => user.value?.email === 'qeetest44@gmail.com' || user.value?.email === 'qetestqfw@quizizz.com');

  const isCorporateUser = computed<boolean>(() => user.value.isCorporate);

  const showIndividualPlan = computed(() => FeatureFlags().isEnabled(Constants.FeatureFlagsTypes.SHOW_INDIVIDUAL_PLAN));

  const isInternalUser = computed(() => user.value?.email?.includes('@quizizz.com'));

  const accountType = computed(() => getAccountType(user.value));

  const isSuperUser = computed(() => accountType.value === Constants.AccountTypes.SUPER);

  const isNewActivationExpUser = computed(() => {
    const { createdAt } = user.value;
    const userCreatedAt = dayjs(createdAt);
    return !!(createdAt && userCreatedAt.isAfter(NEW_USER_ACTIVATION_TRACKING_DATE));
  });

  const isNonActivatedUser = computed(() => {
    const { events = {} } = user.value;
    return !!(isNewActivationExpUser.value && (Boolean(events?.activatedAt) === false));
  });

  const isUserAccountAboveSuper = computed(() => {
    switch (accountType.value) {
      case Constants.AccountTypes.ANONYMOUS:
      case Constants.AccountTypes.BASIC:
      case Constants.AccountTypes.SUPER:
        return false;
      default:
        return true;
    }
  });

  const isBasicUser = computed(() => accountType.value === Constants.AccountTypes.BASIC
    || accountType.value === Constants.AccountTypes.ANONYMOUS);

  const isBasicUSAccount = computed(() => user.value.isBasicUSAccount);
  const isPremiumUser = computed(() => !isBasicUser.value);

  const isUserInUS = computed(() => store.getters['root/isUserInUS']);

  const organizationId: ComputedRef<string | null> = computed(() => user.value
    ? (user.value.organizationIds?.[0] ?? user.value.organization ?? null)
    : null);

  const isPartOfOtherOrganization = computed<boolean>(() => Boolean(organizationId.value && isOtherOrganization(organizationId.value)));

  const paidOrganizationId: ComputedRef<string | null> = computed(() => user.value.paidOrganization ?? null);
  const selectedPaidOrg: ComputedRef<string | null> = computed(() => user.value.selectedPaidOrg);

  const isSnDUser = computed(() => accountType.value === Constants.AccountTypes.SCHOOL_AND_DISTRICT);

  const isSnDTrialUser = computed<boolean>(() => {
    const subscription = user.value.subscriptions[0];
    return Boolean(isSnDUser.value && subscription.planId === 'snd_trial' && subscription.status === 'active');
  });

  async function refreshUser() {
    const user: UserService = await UserActionsService.getCurrentUser($axios);
    if (user) {
      store.dispatch('root/setUser', user);
      eventBus.$emit('userSet', { user });
    }
  }

  const isCorporateAdmin = computed(() => user.value.isCorporate && user.value.isAdmin);

  const grades = computed(() => user.value.grades || []);

  return {
    hasPrivateContentAccess,
    user,
    isLoggedIn,
    isCorporateUser,
    isNonActivatedUser,
    isNewActivationExpUser,
    accountType,
    organizationId,
    isPartOfOtherOrganization,
    paidOrganizationId,
    showIndividualPlan,
    isPremiumUser,
    isUserAccountAboveSuper,
    isBasicUser,
    isUserInUS,
    isBasicUSAccount,
    isE2ETestAccount,
    isInternalUser,
    selectedPaidOrg,
    isSnDUser,
    isSnDTrialUser,
    refreshUser,
    isCorporateAdmin,
    isSuperUser,
    grades,
  };
}
